/**
 * Who is typing, where, and until when — V8-02 §3.
 *
 * ## The bug this replaces
 *
 * The indicator was a `string[]` rendered behind `{typingFrom && …}`. An array
 * is always truthy, so the element was **always** in the document; and an empty
 * array is neither length 1 nor length 2, so it fell through to the final
 * branch of the ternary chain:
 *
 * ```tsx
 * {typingFrom && (
 *   <div>{typingFrom.length === 1 ? … : typingFrom.length === 2 ? … : 'Several people are typing…'}</div>
 * )}
 * ```
 *
 * "Several people are typing…", permanently, in every conversation, with nobody
 * typing. Exactly the founder's report, and a one-character predicate away.
 *
 * ## Why it is a module rather than a `.length > 0`
 *
 * Because the one-character fix leaves the same shape standing, and §3 asks for
 * four properties the old shape could not express: state **per conversation**,
 * expiry **without a stop event**, clearing on send and on disconnect, and never
 * rendering for a conversation other than the one that emitted it.
 *
 * The load-bearing decision is that `describeTyping` returns **null** for an
 * empty list. The component renders nothing when it gets null, so "nobody is
 * typing" has one representation and it is not a string. There is no arrangement
 * of this data that can produce a sentence about people who are not there.
 */

/** §3 — "auto-expires ≤5s after the last typing event". */
export const TYPING_TTL_MS = 5_000;

export interface TypingEntry {
  name: string;
  /** Wall-clock ms after which this person is no longer considered typing. */
  expiresAt: number;
}

/** Conversation id → who is typing in it. Nothing is global. */
export type TypingState = Readonly<Record<string, readonly TypingEntry[]>>;

export const EMPTY_TYPING: TypingState = {};

/**
 * Record a typing ping, refreshing the sender's deadline.
 *
 * Every ping extends only that person in only that conversation, which is what
 * makes the state per-conversation rather than a list that happens to be
 * filtered on the way out.
 */
export function noteTyping(
  state: TypingState,
  event: { conversationId: string; name: string },
  now: number,
): TypingState {
  const existing = state[event.conversationId] ?? [];
  const others = existing.filter((entry) => entry.name !== event.name);
  return {
    ...state,
    [event.conversationId]: [...others, { name: event.name, expiresAt: now + TYPING_TTL_MS }],
  };
}

/**
 * Everyone still typing in one conversation, oldest ping first.
 *
 * Expiry is applied here as well as in `pruneExpired` so that a render between
 * prunes cannot show somebody who has already timed out. The clock is a
 * parameter, never `Date.now()` inside, so the tests can pin it.
 */
export function activeTypers(state: TypingState, conversationId: string, now: number): string[] {
  return (state[conversationId] ?? [])
    .filter((entry) => entry.expiresAt > now)
    .map((entry) => entry.name);
}

/** Drop everything expired, everywhere. Called on a timer by the component. */
export function pruneExpired(state: TypingState, now: number): TypingState {
  const next: Record<string, readonly TypingEntry[]> = {};
  let changed = false;

  for (const [conversationId, entries] of Object.entries(state)) {
    const live = entries.filter((entry) => entry.expiresAt > now);
    if (live.length !== entries.length) changed = true;
    if (live.length > 0) next[conversationId] = live;
  }

  // Returning the same reference when nothing expired keeps React from
  // re-rendering the whole thread once a second for no reason.
  return changed ? next : state;
}

/**
 * §3 — "cleared on message send".
 *
 * When somebody's message arrives, they have manifestly stopped typing, and
 * waiting out the remaining TTL leaves "Ada is typing…" sitting under Ada's
 * message. Dropping one person rather than the conversation: in a group the
 * other two may still be mid-sentence.
 */
export function stopTyping(
  state: TypingState,
  event: { conversationId: string; name: string },
): TypingState {
  const existing = state[event.conversationId];
  if (!existing) return state;

  const remaining = existing.filter((entry) => entry.name !== event.name);
  if (remaining.length === existing.length) return state;

  const next = { ...state };
  if (remaining.length > 0) next[event.conversationId] = remaining;
  else delete next[event.conversationId];
  return next;
}

/**
 * The sentence, or **null** when there is no sentence to write.
 *
 * Null rather than an empty string because a caller must not be able to render
 * the result of "nobody is typing" as anything at all — which is the failure
 * this whole module exists to make unrepresentable.
 */
export function describeTyping(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return 'Several people are typing…';
}
